import React, { useState } from "react";
import { Phone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { studioInfo, goals } from "../../mock";
import SectionLabel from "./SectionLabel";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: "", email: "", phone: "", goal: "", message: "", consent: false };

const Contact = () => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Compila nome, email e telefono.");
      return;
    }
    if (!form.consent) {
      toast.error("Devi acconsentire al trattamento dei dati.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contacts`, form);
      toast.success("Richiesta inviata! Ti contattiamo entro 24 ore.");
      setForm(emptyForm);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore durante l'invio. Riprova.";
      toast.error(typeof msg === "string" ? msg : "Errore durante l'invio. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "bg-white/[0.05] border border-white/20 rounded-md h-12 py-3 px-4 text-white text-base placeholder:text-white/45 focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:border-white/60 transition-colors";

  const labelCls = "block font-mono-label text-[11px] text-white/75 mb-2";

  return (
    <section id="contatti" className="bg-[#0c0c0d] border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
        <SectionLabel>Inizia oggi</SectionLabel>

        <div className="grid md:grid-cols-2 gap-12 md:gap-24 mt-8 items-start">
          <Reveal>
            <h2 className="font-display text-4xl md:text-7xl text-white">
              Inizia ora, prenota la tua{" "}
              <span style={{ fontStyle: "italic", fontWeight: 800 }} className="text-white/45">
                consulenza.
              </span>
            </h2>
            <p className="text-white/65 text-base md:text-lg leading-relaxed mt-8 max-w-[440px]">
              Lascia il tuo numero o email. Ti contattiamo entro 24 ore per fissare la tua
              consulenza iniziale senza impegno.
            </p>

            <div className="mt-12">
              <div className="font-mono-label text-[10px] text-white/40 mb-3">Oppure chiamaci</div>
              <a
                href={`tel:${studioInfo.phoneHref}`}
                className="flex items-center gap-3 font-display text-3xl md:text-4xl text-white hover:text-white/70 transition-colors duration-300"
              >
                <Phone size={26} strokeWidth={1.5} /> {studioInfo.phone}
              </a>
              <div className="font-mono-label text-[10px] text-white/40 mt-4">
                Nessun impegno · Risposta rapida
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <form onSubmit={handleSubmit} className="flex flex-col gap-6 bg-white/[0.03] border border-white/12 rounded-xl p-7 md:p-9">
              <div>
                <label className={labelCls}>Nome e cognome *</label>
                <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Mario Rossi" className={inputCls} />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls}>Email *</label>
                  <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="mario@email.it" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Telefono *</label>
                  <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="375 000 0000" className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Obiettivo</label>
                <Select value={form.goal} onValueChange={(v) => update("goal", v)}>
                  <SelectTrigger className={`${inputCls} justify-between`}>
                    <SelectValue placeholder="Seleziona…" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141415] border-white/15 text-white">
                    {goals.map((g) => (
                      <SelectItem key={g} value={g} className="focus:bg-white/10 focus:text-white">
                        {g}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls}>Messaggio (facoltativo)</label>
                <Textarea value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Raccontaci qualcosa in più…" rows={3} className="bg-white/[0.05] border border-white/20 rounded-md py-3.5 px-4 text-white text-base placeholder:text-white/45 focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:border-white/60 resize-none transition-colors" />
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={form.consent} onCheckedChange={(v) => update("consent", !!v)} className="mt-0.5 border-white/40 data-[state=checked]:bg-white data-[state=checked]:text-black" />
                <span className="text-white/70 text-sm leading-relaxed">
                  Acconsento al trattamento dei dati personali ai fini di essere ricontattato per la
                  consulenza iniziale.
                </span>
              </label>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-3 bg-white text-black font-mono-label text-[12px] px-8 py-4 rounded-md hover:bg-white/85 transition-colors duration-300 disabled:opacity-60"
              >
                {submitting ? "Invio in corso…" : "Prenota la consulenza"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
